import { useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useLang } from '../../../../../../../hooks/use-lang';
import { getBaseUrl } from '../../../../../../../store/api/utils.ts/stand';
import { useQrTemplatesStore } from '../../../../../../../store/use-qr-templates-store';
import { IdValue } from '../../../../../../../types/props';
import { qrTemplates, qrTextPlacements, qrVariants } from './helpers';

const maxToPrint = 50;

export const usePrintQrModal = () => {
  const [size, setSize] = useState(30);
  const [qrCodes, setQrCodes] = useState<IdValue[]>([]);
  const { t } = useLang();

  const [template, setTemplate] = useState<IdValue>({
    ...qrTemplates[0],
    value: t(qrTemplates[0].value),
  });
  const [variant, setVariant] = useState<IdValue>({
    ...qrVariants[0],
    value: t(qrVariants[0].value),
  });
  const [textPlacement, setTextPlacement] = useState<IdValue>({
    ...qrTextPlacements[0],
    value: t(qrTextPlacements[0].value),
  });

  const [background, setBackground] = useState<string>('#ffffff');
  const [dots, setDots] = useState<string>('#000000');
  const [textColor, setTextColor] = useState<string>('#000000');

  const { templates } = useQrTemplatesStore();

  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    copyStyles: true,
  });

  const [duplicate, setDuplicate] = useState<string>('1');

  const [from, setFrom] = useState(0);

  const duplicatedQrCodes: IdValue[] = useMemo(() => {
    let result: IdValue[] = [];
    if (qrCodes.length >= maxToPrint && duplicate === '1') {
      result = qrCodes.slice(from, from + maxToPrint);
    } else {
      const allCodes = qrCodes.reduce((acc: IdValue[], item) => {
        const multiplied = Array.from({ length: Number(duplicate) }, () => item);
        return [...acc, ...multiplied];
      }, []);
      result = allCodes.slice(from, from + maxToPrint);
    }
    const withDomain = result.map((item) => ({ ...item, id: getBaseUrl() + '/qr/' + item.id }));
    return withDomain;
  }, [duplicate, from, qrCodes]);

  const [logo, setLogo] = useState<string>();

  const handleUploadLogo = (file?: File) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      return () => reader.abort();
    } else {
      setLogo('');
    }
  };

  return {
    logo,
    handleUploadLogo,
    setQrCodes,
    t,
    handlePrint,
    setDuplicate,
    duplicate,
    duplicatedQrCodes,
    qrCodes,
    componentRef,
    size,
    setSize,
    template,
    setTemplate,
    variant,
    setVariant,
    selectedTemplate: templates?.find((item) => item.id === template?.id),
    textPlacement,
    setTextPlacement,
    setFrom,
    from,
    maxToPrint,
    background,
    setBackground,
    dots,
    setDots,
    textColor,
    setTextColor,
  };
};
